import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferences,
  Container,
  Header,
  Pagination,
  Table,
  Badge,
  Grid,
} from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useState } from 'react';
import { CollectionPreferencesProps } from '@amzn/awsui-components-react-v3/uxdg';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment';
import { transformPoliciesToRules, renderRules } from './helper';
import { ContainerHistoryInfo, ParentLocation } from '../../../services/container-management/types';

import { getFlattenedKeys } from '../../../utils';

export type ContainerHistoryDiff = {
  timestamp: Date;
  user: string;
  property: string;
  previous: string;
  next: string;
};

// Convert ContainerHistoryInfo response from GraphQL into list of diffs
export const convertContainerHistoryToDiff = (
  containerHistoryInfo: ContainerHistoryInfo
): ContainerHistoryDiff[] => {
  if (!containerHistoryInfo || containerHistoryInfo.history.length === 0) {
    return [];
  }

  // First "diff" should be the container creation
  const output = [
    {
      timestamp: containerHistoryInfo.history[0].versionData.updatedDateTime,
      user: containerHistoryInfo.history[0].versionData.user,
      property: 'created',
      previous: '',
      next: '',
    },
  ];

  // Loop through history and find diffs
  for (let i = 1; i < containerHistoryInfo.history.length; i += 1) {
    // Get all the property names from previous and current version
    const properties = _.union(
      getFlattenedKeys(containerHistoryInfo.history[i - 1]),
      getFlattenedKeys(containerHistoryInfo.history[i])
    );
    // For each property, check if it was different between previous and current version
    properties.forEach((property) => {
      // These are always different and should be ignored.
      // There are both metadataStruct and metadata, so we should ignore one of them.
      if (
        property === 'version' ||
        property.startsWith('versionData.') ||
        property.startsWith('metadataStruct.') ||
        property.startsWith('isActive') ||
        property.startsWith('locationId')
      ) {
        return;
      }
      const previous = _.get(containerHistoryInfo.history[i - 1], property);
      const next = _.get(containerHistoryInfo.history[i], property);

      if (!_.isEqual(previous, next)) {
        const diff = {
          timestamp: containerHistoryInfo.history[i].versionData.updatedDateTime,
          user: containerHistoryInfo.history[i].versionData.user,
        };
        // TODO handle this formatting generically, rather than using switch
        switch (property) {
          case 'state':
            Object.assign(diff, {
              property: 'state',
              previous,
              next,
            });
            break;
          case 'parentLocation':
            Object.assign(diff, {
              property: 'location',
              previous: renderLocations(previous),
              next: renderLocations(next),
            });
            break;
          case 'seals':
            Object.assign(diff, {
              property: 'seal',
              previous: renderSeals(previous),
              next: renderSeals(next),
            });
            break;
          case 'tickets':
            Object.assign(diff, {
              property: 'ticket',
              previous: renderTickets(previous),
              next: renderTickets(next),
            });
            break;
          case 'policyIds': {
            const previousRules = renderRules(
              getRulesFromPolicyMap(containerHistoryInfo.policyMap, previous.sort())
            );
            const nextRules = renderRules(
              getRulesFromPolicyMap(containerHistoryInfo.policyMap, next.sort())
            );
            if (!_.isEqual(previousRules, nextRules)) {
              Object.assign(diff, {
                property: 'rules',
                previous: previousRules,
                next: nextRules,
              });
              break;
            } else {
              // If after transforming to rules there is no difference, do not output a diff.
              return;
            }
          }
          default:
            Object.assign(diff, {
              property,
              previous,
              next,
            });
            break;
        }
        output.push(diff);
      }
    });
  }

  return output;
};

export const renderLocations = (parentLocations: ParentLocation[]): ReactElement | string => {
  if (parentLocations === undefined || parentLocations === null || parentLocations.length === 0)
    return 'N/A';

  const badges = parentLocations.map((location) => {
    // eslint-disable-next-line no-nested-ternary
    // TODO We need use translation t(`container-management.location.type`)
    return (
      <Badge color="green">
        {location.type.toLowerCase()}: {location.value}
      </Badge>
    );
  });
  return badges.length > 0 ? <Grid>{badges}</Grid> : 'N/A';
};

export const renderSeals = (seals) => {
  if (seals === undefined || seals === null || seals.length === 0) return 'N/A';

  const badges = seals.map((seal) => {
    // eslint-disable-next-line no-nested-ternary
    return <Badge color="green">{seal.sealId}</Badge>;
  });
  return badges.length > 0 ? <Grid>{badges}</Grid> : 'N/A';
};

export const renderTickets = (tickets) => {
  if (tickets === undefined || tickets === null || tickets.length === 0) return 'N/A';

  const badges = tickets.map((ticket) => {
    // eslint-disable-next-line no-nested-ternary
    return <Badge color="green">{ticket.ticketId}</Badge>;
  });
  return badges.length > 0 ? <Grid>{badges}</Grid> : 'N/A';
};

export const getRulesFromPolicyMap = (policyMap, policyIds: string[]) => {
  const policies = _.values(_.pick(policyMap, policyIds));
  // Convert uppercase Effect/Action/Resource to lowercase
  policies.forEach((policy) => {
    policy.statements.forEach((statement) => {
      if (statement.Effect) {
        // eslint-disable-next-line no-param-reassign
        statement.effect = statement.Effect;
      }
      if (statement.Action) {
        // eslint-disable-next-line no-param-reassign
        statement.action = statement.Action;
      }
      if (statement.Resource) {
        // eslint-disable-next-line no-param-reassign
        statement.resource = statement.Resource;
      }
    });
  });

  return Array.from(transformPoliciesToRules(policies).values());
};

const ContainerHistory = (props: {
  data: ContainerHistoryDiff[];
  loading: boolean;
}): ReactElement => {
  const { data, loading } = props;
  const { t } = useTranslation();

  const columnDefinitions = [
    {
      id: 'timestamp',
      header: 'Date',
      cell: (item: ContainerHistoryDiff) => moment(item.timestamp).format('YYYY-MM-DD HH:mm:ss'),
      sortingField: 'timestamp',
    },
    {
      id: 'user',
      header: 'User',
      cell: (item: ContainerHistoryDiff) => item.user,
      sortingField: 'user',
    },
    {
      id: 'property',
      header: 'Property',
      cell: (item: ContainerHistoryDiff) =>
        t(`container-management.container-history.${item.property}`),
      sortingField: 'property',
    },
    {
      id: 'previous',
      header: 'Past value',
      cell: (item: ContainerHistoryDiff) => translateValue(item.previous),
      sortingField: 'previous',
    },
    {
      id: 'next',
      header: 'New value',
      cell: (item: ContainerHistoryDiff) => translateValue(item.next),
      sortingField: 'next',
    },
  ];

  const translateValue = (value: string): string => {
    // if (value === 'ACTIVE') return t('Active');
    // if (value === 'INACTIVE') return t('Inactive');
    return value;
  };

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    pageSize: 10,
    visibleContent: ['timestamp', 'user', 'property', 'previous', 'next'],
  });

  const { items, collectionProps, paginationProps } = useCollection(data, {
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: { sortingColumn: columnDefinitions[0] } },
  });

  return (
    <div>
      {items.length > 0 && (
        <Table
          {...collectionProps}
          columnDefinitions={columnDefinitions}
          items={items}
          header={<Header>Container History</Header>}
          visibleColumns={preferences.visibleContent}
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNum) => `Page ${pageNum} of all pages`,
              }}
            />
          }
          loading={loading}
          preferences={
            <CollectionPreferences
              title="Preferences"
              onConfirm={({ detail }) => setPreferences(detail)}
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={preferences}
              pageSizePreference={{
                title: 'Select page size',
                options: [
                  { value: 5, label: '5 items' },
                  { value: 10, label: '10 items' },
                  { value: 20, label: '20 items' },
                  { value: 50, label: '50 items' },
                  { value: 100, label: '100 items' },
                  { value: 1000, label: '1000 items' },
                ],
              }}
              visibleContentPreference={{
                title: 'Select visible content',
                options: [
                  {
                    label: 'Main distribution properties',
                    options: [
                      { id: 'timestamp', label: 'Date' },
                      { id: 'user', label: 'User' },
                      { id: 'property', label: 'Property Type' },
                      { id: 'previous', label: 'Past Value' },
                      { id: 'next', label: 'New Value' },
                    ],
                  },
                ],
              }}
            />
          }
        />
      )}
      {items.length === 0 && (
        <Container header={<Header>Container History</Header>}>
          <Box color="text-status-inactive" textAlign="center">
            No container history to display.
          </Box>
        </Container>
      )}
    </div>
  );
};

export default ContainerHistory;
